import api from './api';
import { PaginatedChatHistory } from '../types';
import type { AxiosResponse } from 'axios';

export const chatService = {
  sendMessage: async (message: string, threadId: string): Promise<any> => {
    const response = await api.post('/api/chat', { message, thread_id: threadId });
    return response;
  },

  sendMessageToThread: async (message: string, threadId: string): Promise<any> => {
    const response = await api.post(`/api/chat/${threadId}/message`, { message });
    return response.data;
  },

  getThreadMessages: async (threadId: string): Promise<any> => {
    return api.get(`/api/chat/${threadId}`);
  },

  deleteThread: async (threadId: string): Promise<any> => {
    return api.delete(`/api/chat/${threadId}`);
  },

  getChatHistory: async (
    page: number = 1,
    pageSize: number = 100,
  ): Promise<AxiosResponse<PaginatedChatHistory>> => {
    return api.get<PaginatedChatHistory>(`/api/chat/history?page=${page}&page_size=${pageSize}`);
  },

  getChatMeta: async (): Promise<any> => {
    const response = await api.get('/api/chat/meta');
    return response.data;
  },

  getCitationPdf: async (citationId: string): Promise<Blob> => {
    const response = await api.get(`/api/chat/citation/${citationId}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  getCitationDetails: async (citationId: string): Promise<any> => {
    const response = await api.get(`/api/chat/citation/${citationId}/meta`);
    return response.data;
  },

  uploadKnowledgeFile: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/knowledge/upload', formData);
    return response.data;
  },

  uploadKnowledgeFilesBatch: async (files: File[]): Promise<any> => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await api.post('/api/knowledge/upload-batch', formData);
    return response.data;
  },

  addKnowledgeFilesBatch: async (
    filenames: string[],
    data: {
      tag?: string;
      type: string;
      parsing_strategy?: string;
      auto_suggest_tag?: boolean;
      as_of_date?: string;
      metadata_tags?: Record<string, string>;
      files?: Array<{
        filename: string;
        tag?: string;
        as_of_date?: string;
        parsing_strategy?: string;
        auto_suggest_tag?: boolean;
        metadata_tags?: Record<string, string>;
      }>;
    },
  ): Promise<any> => {
    return api.post('/api/knowledge/add-files-batch', {
      filenames,
      tag: data.tag,
      type_: data.type,
      parsing_strategy: data.parsing_strategy,
      auto_suggest_tag: data.auto_suggest_tag,
      as_of_date: data.as_of_date,
      metadata_tags: data.metadata_tags,
      files: data.files,
    });
  },

  getBatchStatus: async (batchId: string): Promise<any> => {
    const response = await api.get(`/api/knowledge/batch/${batchId}/status`);
    return response.data;
  },

  addKnowledge: async (data: { text: string; tag?: string; type?: string; metadata_tags?: Record<string, string>; as_of_date?: string }): Promise<any> => {
    return api.post('/api/knowledge/add', {
      knowledge: data.text,
      tag: data.tag,
      type_: data.type || 'N/A',
      metadata_tags: data.metadata_tags || {},
      as_of_date: data.as_of_date,
    });
  },

  addKnowledgeFile: async (
    filename: string,
    data: {
      tag?: string;
      type: string;
      parsing_strategy?: string;
      auto_suggest_tag?: boolean;
      as_of_date?: string;
      metadata_tags?: Record<string, string>;
    },
  ): Promise<any> => {
    return api.post('/api/knowledge/add-file', {
      filename,
      tag: data.tag,
      type_: data.type,
      parsing_strategy: data.parsing_strategy,
      auto_suggest_tag: data.auto_suggest_tag,
      as_of_date: data.as_of_date,
      metadata_tags: data.metadata_tags,
    });
  },

  getKnowledgeStrategyOptions: async (filename: string): Promise<any> => {
    const response = await api.get(`/api/knowledge/strategy-options?filename=${encodeURIComponent(filename)}`);
    return response.data;
  },

  getKnowledgeFileStatus: async (fileUuid: string): Promise<any> => {
    const response = await api.get(`/api/knowledge/${fileUuid}/status`);
    return response.data;
  },

  validateTag: async (fileUuid: string, proposedTag: string): Promise<any> => {
    const response = await api.post('/api/knowledge/validate-tag', {
      file_uuid: fileUuid,
      proposed_tag: proposedTag,
    });
    return response.data;
  },

  confirmTag: async (
    fileUuid: string,
    finalTag: string,
    userOverrideSuggestion: boolean = false,
  ): Promise<any> => {
    const response = await api.post('/api/knowledge/confirm-tag', {
      file_uuid: fileUuid,
      final_tag: finalTag,
      user_overrode_suggestion: userOverrideSuggestion,
    });
    return response.data;
  },

  getIngestedDocuments: async (): Promise<any> => {
    const response = await api.get('/api/documents');
    return response.data;
  },

  getIngestedDocumentFile: async (fileId: string): Promise<Blob> => {
    const response = await api.get(`/api/documents/file/${fileId}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  getIngestedDocumentFileByVariant: async (
    fileId: string,
    variant: 'original' | 'processed' = 'original',
  ): Promise<Blob> => {
    const response = await api.get(`/api/documents/file/${fileId}?variant=${variant}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  getIngestedDocumentFileVariants: async (
    fileId: string,
  ): Promise<{
    file_id: string;
    original_available: boolean;
    processed_available: boolean;
    original_name: string;
    processed_name: string;
  }> => {
    const response = await api.get(`/api/documents/file/${fileId}/variants`);
    return response.data;
  },

  deleteIngestedDocument: async (fileId: string): Promise<any> => {
    const response = await api.delete(`/api/documents/${fileId}`);
    return response.data;
  },

  getUploadedTextEntries: async (): Promise<any> => {
    const response = await api.get('/api/knowledge/texts');
    return response.data;
  },

  deleteUploadedTextEntry: async (textId: string): Promise<any> => {
    const response = await api.delete(`/api/knowledge/texts/${textId}`);
    return response.data;
  },

  updateUploadedTextEntry: async (
    textId: string,
    data: { text: string; fund: string; metadata_tags?: Record<string, string>; as_of_date?: string },
  ): Promise<any> => {
    const response = await api.patch(`/api/knowledge/texts/${textId}`, data);
    return response.data;
  },

  updateDocumentMetadata: async (
    fileId: string,
    metadata: Record<string, string>,
  ): Promise<any> => {
    const response = await api.patch(`/api/documents/${fileId}/metadata`, {
      documents_metadata: metadata,
    });
    return response.data;
  },

  getDocumentVectorCounts: async (): Promise<Record<string, number>> => {
    const response = await api.get('/api/documents/vector-counts');
    return response.data.file_vector_counts;
  },

};
